import React, { useMemo, useState } from "react";

import {
	Box,
	Button,
	CircularProgress,
	Container,
	IconButton,
	Snackbar,
	TextField,
	Typography
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";

import { StripeElementChangeEvent, StripeElementType } from "@stripe/stripe-js";

import { useTranslation } from "react-i18next";

import { useNavigate } from "react-router";

import CartItem from "components/Cart/CartItem";
import { PromoCode } from "components/PromoCode";
import ScrollBars from "components/ScrollBars";
import StripeTextField from "components/Stripe";
import { Terms } from "components/Terms";
import { Tip } from "components/Tip";
import makeStyles from "hooks/makeStyles";
import useCart from "hooks/useCart";
import CreditCard from "icons/CreditCardIcon";
import EditCircle from "icons/EditCircle";
import SignOutAltIcon from "icons/SignOutAltIcon";
import TrashIcon from "icons/TrashIcon";
import { formatMoney } from "utility/formatMoney";

import CustomerDesktopInfo from "./CustomerInfo";

import OrderTotal from "./OrderTotal";
import OrderTypeInfo from "./OrderTypeInfo";

import customerInfoValidate from "../CustomerInfo/validate";

const useStyles = makeStyles(theme => ({
	root: {
		padding: 0,
		display: "flex",
		flex: 1,
		flexDirection: "column",
		height: "calc(100vh - 135px)", // not great..
		position: "relative",
		[theme.breakpoints.up(480)]: {
			overflow: "hidden",
			height: "100vh"
		}
	},
	boxDivider: {
		borderTop: "1px solid #888",
		margin: theme.spacing(1),
		width: "100%"
	},
	container: {
		display: "flex",
		flexDirection: "column",
		padding: 0,
		paddingTop: theme.spacing(1)
	},
	cart: {
		display: "flex",
		flexDirection: "column",
		padding: 0,
		paddingTop: theme.spacing(1),
		borderBottom: "1px solid #888"
	},
	availabilityWarningRow: {
		margin: 0
	},
	availabilityWarning: {
		fontSize: "14px",
		color: theme.palette.warning.main
	},
	titleRow: {
		marginBottom: theme.spacing(1),
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between"
	},
	titleBlock: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between"
	},
	title: {
		fontSize: 18,
		textTransform: "uppercase",
		fontWeight: 500,
		color: "#fff",
		[theme.breakpoints.up(480)]: {
			color: theme.palette.text.primary
		}
	},
	text: {
		fontSize: 12,
		color: "#fff",
		textTransform: "uppercase",
		[theme.breakpoints.up(480)]: {
			color: theme.palette.text.primary
		}
	},
	iconButton: {
		padding: 0,
		marginLeft: theme.spacing(1),
		"&:hover": {
			backgroundColor: "transparent"
		}
	},
	icon: {
		marginRight: 10,
		fill: "#fff",
		[theme.breakpoints.up(480)]: {
			fill: theme.palette.text.primary
		}
	},
	clearButton: {
		minWidth: "auto",
		marginLeft: theme.spacing(1),
		verticalAlign: "top",
		marginTop: "4px"
	},
	trashIcon: {
		height: 18,
		marginLeft: theme.spacing(1),
		fill: theme.palette.text.primary
	},
	body: {
		[theme.breakpoints.up(480)]: {
			backgroundColor: theme.palette.background.default,
			color: theme.palette.text.primary,
			borderBottomLeftRadius: theme.spacing(2),
			borderBottomRightRadius: theme.spacing(2),
			padding: theme.spacing(4)
		}
	},

	items: {
		marginBottom: theme.spacing(2)
	},
	scroll: {
		height: "calc(100vh - 225px)"
	},
	itemDivider: {
		marginTop: theme.spacing(2),
		backgroundColor: "#f2f2f2"
	},
	item: {
		marginLeft: theme.spacing(2),
		marginTop: theme.spacing(2),
		marginRight: theme.spacing(2),
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between"
	},
	itemTitle: {
		color: "#f2f2f2",
		textTransform: "uppercase",
		fontSize: "12px",
		letterSpacing: "0.38px"
	},
	itemTitleChecked: {
		color: "#fff"
	},
	radioIcon: {
		borderRadius: "50%",
		width: 13,
		height: 13,
		border: "1px solid #424242"
	},
	radioIconChecked: {
		borderRadius: "50%",
		width: 13,
		height: 13,
		border: "1px solid #fff",
		backgroundImage: "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
		"&:before": {
			display: "block",
			width: 11,
			height: 11,
			backgroundImage: "radial-gradient(#BDBDBD,#BDBDBD 56%,transparent 32%)",
			content: '""'
		}
	},
	footer: {
		backgroundColor: "transparent",
		bottom: 0,
		left: 0,
		right: 0,
		width: "100%",
		marginTop: theme.spacing(1),
		marginRight: theme.spacing(1),
		padding: 0,
		position: "absolute",
		[theme.breakpoints.up(480)]: {
			marginTop: 0,
			width: "auto",
			bottom: "6px",
			flex: "1",
			justifyContent: "center",
			padding: theme.spacing(2)
		}
	},
	addButton: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		borderWidth: 0,
		borderRadius: 24,
		fontSize: 16,
		height: 48,
		width: "100%",
		padding: 0,
		alignContent: "center",
		justifyContent: "center",
		textTransform: "uppercase",
		"&:hover": {
			[theme.breakpoints.up(480)]: {
				backgroundColor: theme.palette.primary.main,
				color: theme.palette.primary.contrastText
			}
		}
	},
	buttonIcon: {
		position: "absolute",
		right: 10,
		color: "#fff"
	},
	actionButtonLabel: {
		marginLeft: theme.spacing(0.5)
	},
	textField: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		borderColor: "#fff",
		color: "#000",
		backgroundColor: "#f2f2f2",
		borderRadius: 8
	},
	test: {
		width: "100%"
	},
	snackbar: {
		width: "100%"
	}
}));

interface Props {
	onChange: (orderTypeId: string) => void;
}

const DesktopCheckoutPanel: React.FC<Props> = ({ onChange }) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { cartItems, grandTotalCents, placeOrder, clearCart, getCustomerInfo, areAllItemsAvailable } = useCart();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isOrderPlacing, setIsOrderPlacing] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);
	const [nameOnCard, setNameOnCard] = useState("");
	const stripe = useStripe();
	const elements = useElements();
	const navigate = useNavigate();
	const [editPaymentMode, setEditPaymentMode] = useState(true);
	const [placeError, setPlaceError] = useState<string | undefined>();

	const customerInfo = getCustomerInfo();
	const allItemsAreAvailable = areAllItemsAvailable();

	const [state, setState] = useState<{
		elementError: { [key in StripeElementType]?: string };
		elementValid: { [key in StripeElementType]?: boolean };
	}>({ elementError: {}, elementValid: {} });

	const onDataChanged = (event: StripeElementChangeEvent) => {
		setHasChanges(true);

		setState({
			...state,
			elementValid: {
				...state.elementValid,
				[event.elementType]: event.complete && !event.empty
			},
			elementError: {
				...state.elementError,
				[event.elementType]: event.error?.message
			}
		});
	};

	const isCustomerValid = Object.keys(customerInfoValidate(customerInfo)).length === 0;
	const isCardValid =
		nameOnCard && state.elementValid.cardNumber && state.elementValid.cardExpiry && state.elementValid.cardCvc;

	const isCheckoutEnabled = cartItems.length > 0 && isCustomerValid && isCardValid;

	const onCustomerInfoUpdated = () => {};

	const clearOrderClicked = () => {
		clearCart();
	};

	const submit = useMemo(
		() => () => {
			if (isOrderPlacing) return;
			setIsSubmitting(true);
			if (isCustomerValid) {
				if (!stripe || !elements) {
					// Stripe.js has not loaded yet. Make sure to disable
					// form submission until Stripe.js has loaded.
					console.error("Stripe.js not loaded");
					return;
				}

				setIsOrderPlacing(true);

				placeOrder({
					stripe,
					element: elements.getElement(CardNumberElement)!
				})
					.then(() => {
						setIsOrderPlacing(false);
						navigate("#");
					})
					.catch(err => {
						setIsOrderPlacing(false);
						setPlaceError(err.message);
					});
			}

			return false;
		},
		[isOrderPlacing, customerInfo, stripe, elements, placeOrder, navigate]
	);

	return (
		<>
			<Container className={classes.root}>
				<form onSubmit={submit}>
					<Box className={classes.body}>
						<ScrollBars className={classes.scroll}>
							<CustomerDesktopInfo isSubmitting={isSubmitting} onCustomerInfoUpdated={onCustomerInfoUpdated} />

							<OrderTypeInfo onSubmit={onCustomerInfoUpdated} />

							<Container className={classes.container}>
								<Box className={classes.titleRow}>
									<Box className={classes.titleBlock}>
										<Typography className={classes.title}>{t("payment")}</Typography>
										<IconButton
											className={classes.iconButton}
											// onClick={() => setEditPaymentMode(!editPaymentMode)} // UNCOMMENT when we'll have save cards
											title={t("edit")}
										>
											<EditCircle width={16} height={16} className={classes.icon} />
										</IconButton>
									</Box>
								</Box>
								{editPaymentMode ? (
									<>
										<TextField
											className={classes.textField}
											fullWidth
											placeholder={t("nameOnCard").toUpperCase()}
											variant="outlined"
											inputProps={{ maxLength: 80 }}
											onChange={e => setNameOnCard(e.target.value)}
											value={nameOnCard}
											required
										/>

										<StripeTextField
											className={classes.textField}
											error={Boolean(state.elementError.cardNumber)}
											// helperText={state.elementError.cardNumber}
											inputProps={{
												options: {
													showIcon: true
												},
												id: "cardNumber"
											}}
											onChange={onDataChanged}
											stripeElement={CardNumberElement}
											required
										/>

										<StripeTextField
											className={classes.textField}
											error={Boolean(state.elementError.cardNumber)}
											// helperText={state.elementError.cardNumber}
											// inputProps={{
											// 	options: {
											// 		showIcon: true
											// 	}
											// }}
											onChange={onDataChanged}
											stripeElement={CardExpiryElement}
											required
										/>

										<StripeTextField
											className={classes.textField}
											error={Boolean(state.elementError.cardNumber)}
											// helperText={state.elementError.cardNumber}
											// inputProps={{
											// 	options: {
											// 		showIcon: true
											// 	}
											// }}
											onChange={onDataChanged}
											stripeElement={CardCvcElement}
										/>
									</>
								) : (
									<Box className={classes.titleRow}>
										<Box className={classes.titleBlock}>
											<Typography className={classes.text}>{t("Enter card details ")} </Typography>
											<Typography className={classes.iconButton}>
												<CreditCard width={16} height={16} className={classes.icon} />
											</Typography>
										</Box>
									</Box>
								)}
							</Container>
							<Box className={classes.boxDivider} />
							<Container className={classes.cart}>
								<Box className={classes.titleRow}>
									<Typography className={classes.title}>{t("cart")}</Typography>
									<Button onClick={clearOrderClicked} className={classes.clearButton} disabled={cartItems.length === 0}>
										<Typography className={classes.text}>{t("clearOrder")}</Typography>
										<TrashIcon className={classes.trashIcon} />
									</Button>
								</Box>
								{cartItems && cartItems.map((c, indx) => <CartItem cartItem={c} key={indx} />)}
							</Container>
							{allItemsAreAvailable ? null : (
								<Box className={classes.availabilityWarningRow}>
									<Typography className={classes.availabilityWarning}>
										Some items in your cart are unavailable at the selected time
									</Typography>
								</Box>
							)}
							<Tip />
							<PromoCode />
							<OrderTotal />
						</ScrollBars>
					</Box>
					<Box className={classes.footer}>
						<Terms />
						<Button
							variant="outlined"
							className={classes.addButton}
							disabled={!isCheckoutEnabled || isOrderPlacing || !allItemsAreAvailable}
							onClick={submit}
						>
							{formatMoney(grandTotalCents)}
							<Typography className={classes.actionButtonLabel}>{` - ${t("placeOrder")}`}</Typography>
							{isOrderPlacing ? (
								<CircularProgress className={classes.buttonIcon} />
							) : (
								<SignOutAltIcon className={classes.buttonIcon} />
							)}
						</Button>
					</Box>
				</form>
			</Container>

			<Snackbar
				open={!!placeError}
				autoHideDuration={6000}
				className={classes.snackbar}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => {
						setPlaceError(undefined);
					}}
					severity="error"
				>
					{`${t("Oppsss")}: ${placeError}`}
				</Alert>
			</Snackbar>
		</>
	);
};

export default DesktopCheckoutPanel;
